<template>
    <div v-if="!loading && chartData.length > 0">
        <h3 class="text-subtitle-1 font-weight-bold mb-1">Preisentwicklung</h3>

        <div class="m-row">
            <!-- Chart links -->
            <div :class="['m-col m-col-12', hasRightContent ? 'm-col-md-6' : '']">
                <div class="chart-wrapper">
                    <!-- Chart area: simple SVG sparkline -->
                    <div class="chart-area">
                        <svg
                            v-if="sparklineValues.length > 1"
                            :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
                            preserveAspectRatio="none"
                            class="sparkline-svg"
                        >
                            <defs>
                                <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stop-color="#3c8f93" stop-opacity="0.4" />
                                    <stop offset="100%" stop-color="#b2d8da" stop-opacity="0.05" />
                                </linearGradient>
                            </defs>
                            <path :d="fillPath" fill="url(#sparkGrad)" />
                            <polyline :points="polylinePoints" fill="none" stroke="#3c8f93" stroke-width="2" stroke-linejoin="round" />
                        </svg>
                    </div>
                    <div class="y-axis">
                        <span
                            v-for="(tick, i) in yTicks"
                            :key="i"
                            class="y-tick text-caption text-medium-emphasis"
                        >
                            {{ formatPrice(tick) }}
                        </span>
                    </div>
                </div>

                <!-- X-Achse -->
                <div class="x-axis d-flex justify-space-between mt-1 mb-3">
                    <span
                        v-for="(label, i) in xLabels"
                        :key="i"
                        class="text-caption text-medium-emphasis"
                    >
                        {{ label }}
                    </span>
                </div>

                <!-- Zeitraum-Buttons -->
                <MBtnToggle
                    :model-value="selectedDays"
                    mandatory
                    density="compact"
                    variant="outlined"
                    divided
                    rounded
                >
                    <MBtn
                        v-for="range in timeRanges"
                        :key="range.days"
                        :value="range.days"
                        size="small"
                        @click="changeDays(range.days)"
                    >
                        {{ range.label }}
                    </MBtn>
                </MBtnToggle>
            </div>

            <!-- Info rechts -->
            <div v-if="hasRightContent" class="m-col m-col-12 m-col-md-6">
                <div class="pl-md-6">
                    <!-- Savings -->
                    <div v-if="savingsInfo" class="mb-4">
                        <div class="d-flex align-center ga-2 mb-1">
                            <span class="text-body-1 font-weight-bold">
                                {{
                                    savingsInfo.isGoodDeal
                                        ? "Jetzt zuschlagen!"
                                        : "Preis beobachten"
                                }}
                            </span>
                            <MChip
                                :color="
                                    savingsInfo.isGoodDeal ? 'success' : 'error'
                                "
                                size="small"
                                variant="flat"
                                label
                            >
                                {{ savingsInfo.percentDiff > 0 ? "+" : ""
                                }}{{ savingsInfo.percentDiff }}&nbsp;%
                                <MIcon
                                    :icon="
                                        savingsInfo.isGoodDeal
                                            ? 'trending-down'
                                            : 'trending-up'
                                    "
                                    :size="14"
                                />
                            </MChip>
                        </div>
                        <p class="text-body-2 text-medium-emphasis">
                            <template v-if="savingsInfo.isGoodDeal">
                                Du sparst heute
                                <strong>{{
                                    formatPrice(savingsInfo.savings)
                                }}</strong>
                                im Vergleich zum
                                {{ selectedDays }}-Tage-Durchschnittspreis.
                            </template>
                            <template v-else>
                                <strong>{{
                                    formatPrice(savingsInfo.savings)
                                }}</strong>
                                über dem
                                {{ selectedDays }}-Tage-Durchschnittspreis.
                            </template>
                        </p>
                    </div>

                    <!-- Aktuell bei Händler -->
                    <div v-if="cheapestMerchant" class="mb-5">
                        <span class="text-body-2 text-medium-emphasis">
                            Aktuell:
                            {{ formatPrice(cheapestMerchant.currentPrice!) }}
                            &nbsp;bei
                            <a
                                v-if="cheapestMerchant.affiliateUrl"
                                :href="cheapestMerchant.affiliateUrl"
                                target="_blank"
                                rel="noopener sponsored"
                                class="text-medium-emphasis text-decoration-underline"
                            >
                                {{ cheapestMerchant.merchantName }}
                            </a>
                            <span v-else class="text-decoration-underline">
                                {{ cheapestMerchant.merchantName }}
                            </span>
                        </span>
                    </div>

                    <!-- Barometer -->
                    <div v-if="barometer" class="mb-6">
                        <div class="barometer-track">
                            <div
                                class="barometer-marker"
                                :style="{
                                    left: barometer.position + '%',
                                    borderColor: barometerColor,
                                }"
                            />
                        </div>
                        <div class="d-flex justify-space-between mt-1">
                            <span class="text-caption text-medium-emphasis">
                                {{ shortPrice(barometer.min) }}
                            </span>
                            <span class="text-caption text-medium-emphasis">
                                {{ shortPrice(barometer.max) }}
                            </span>
                        </div>
                    </div>

                    <!-- Action buttons -->
                    <div class="d-flex flex-wrap ga-4 align-end">
                        <div v-if="cheapestMerchant?.affiliateUrl">
                            <div class="text-caption text-medium-emphasis mb-2">
                                Preis gefällt?
                            </div>
                            <MBtn
                                color="success"
                                variant="flat"
                                size="default"
                                :href="cheapestMerchant.affiliateUrl"
                                append-icon="open-in-new"
                            >
                                Zum Shop
                            </MBtn>
                        </div>
                        <div>
                            <div class="text-caption text-medium-emphasis mb-2">
                                Preiswecker stellen
                            </div>
                            <MBtn
                                variant="outlined"
                                size="default"
                                prepend-icon="alarm"
                                @click="$emit('set-price-alert')"
                            >
                                Preiswecker stellen
                            </MBtn>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { usePriceHistory } from "~/composables/usePriceHistory";

const props = defineProps<{ ean: string; days?: number }>();
defineEmits<{ "set-price-alert": [] }>();

const {
    data: priceData,
    loading,
    fetchHistory,
} = usePriceHistory(computed(() => props.ean));

const timeRanges = [
    { label: "3 Monate", days: 90 },
    { label: "6 Monate", days: 180 },
    { label: "1 Jahr", days: 365 },
];
const selectedDays = ref(props.days || 90);
const changeDays = (d: number) => {
    selectedDays.value = d;
    fetchHistory(d);
};

// All price points from all merchants, sorted by date
const allPrices = computed(() => {
    if (!priceData.value) return [];
    const points: { price: number; date: string }[] = [];
    for (const m of priceData.value.merchants) {
        for (const p of m.history) {
            points.push({ price: p.price, date: p.recordedAt });
        }
    }
    points.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
    return points;
});

const chartData = computed(() => {
    if (!priceData.value) return [];
    return priceData.value.merchants.filter((m) => m.history.length > 0);
});

const sparklineValues = computed(() => {
    const pts = allPrices.value;
    const first = pts[0];
    if (!first) return [];
    if (pts.length === 1) return [first.price, first.price];
    return pts.map((p) => p.price);
});

// SVG sparkline dimensions
const svgWidth = 400;
const svgHeight = 160;
const svgPadding = 8;

const polylinePoints = computed(() => {
    const vals = sparklineValues.value;
    if (vals.length < 2) return '';
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const w = svgWidth - svgPadding * 2;
    const h = svgHeight - svgPadding * 2;
    return vals.map((v, i) => {
        const x = svgPadding + (i / (vals.length - 1)) * w;
        const y = svgPadding + (1 - (v - min) / range) * h;
        return `${x},${y}`;
    }).join(' ');
});

const fillPath = computed(() => {
    const vals = sparklineValues.value;
    if (vals.length < 2) return '';
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const w = svgWidth - svgPadding * 2;
    const h = svgHeight - svgPadding * 2;
    const points = vals.map((v, i) => {
        const x = svgPadding + (i / (vals.length - 1)) * w;
        const y = svgPadding + (1 - (v - min) / range) * h;
        return `${x},${y}`;
    });
    const first = points[0]?.split(',') ?? ['0', '0'];
    const last = points[points.length - 1]?.split(',') ?? ['0', '0'];
    return `M${first[0]},${svgHeight} L${points.join(' L')} L${last[0]},${svgHeight} Z`;
});

// Y-Achse: 5 gleichmäßig verteilte Ticks
const yTicks = computed(() => {
    const vals = sparklineValues.value;
    if (vals.length === 0) return [];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    if (max - min < 1) return [min];
    const steps = 4;
    const step = (max - min) / steps;
    const ticks: number[] = [];
    for (let i = steps; i >= 0; i--) {
        ticks.push(min + step * i);
    }
    return ticks;
});

// X-Achse: 4 gleichmäßig verteilte Datumslabels
const xLabels = computed(() => {
    const pts = allPrices.value;
    if (pts.length < 2) return [];
    const fmt = new Intl.DateTimeFormat("de-DE", {
        day: "numeric",
        month: "short",
    });
    const count = 4;
    const labels: string[] = [];
    for (let i = 0; i < count; i++) {
        const idx = Math.round((i / (count - 1)) * (pts.length - 1));
        const pt = pts[idx];
        if (pt) labels.push(fmt.format(new Date(pt.date)));
    }
    return labels;
});

const avgPrice = computed(() => {
    const pts = allPrices.value;
    if (pts.length === 0) return 0;
    return pts.reduce((s, p) => s + p.price, 0) / pts.length;
});

const currentPrice = computed(() => {
    if (!priceData.value) return 0;
    const ps = priceData.value.merchants
        .filter((m) => m.currentPrice != null)
        .map((m) => m.currentPrice as number);
    return ps.length > 0 ? Math.min(...ps) : 0;
});

const cheapestMerchant = computed(() => {
    if (!priceData.value) return null;
    const active = priceData.value.merchants.filter(
        (m) => m.currentPrice != null,
    );
    if (active.length === 0) return null;
    return active.reduce((a, b) =>
        (a.currentPrice ?? Infinity) <= (b.currentPrice ?? Infinity) ? a : b,
    );
});

const priceMin = computed(() => {
    const pts = allPrices.value;
    if (pts.length === 0) return 0;
    let min = Infinity;
    for (const p of pts) if (p.price < min) min = p.price;
    if (currentPrice.value && currentPrice.value < min)
        min = currentPrice.value;
    return min;
});

const priceMax = computed(() => {
    const pts = allPrices.value;
    if (pts.length === 0) return 100;
    let max = 0;
    for (const p of pts) if (p.price > max) max = p.price;
    if (currentPrice.value && currentPrice.value > max)
        max = currentPrice.value;
    return max;
});

const savingsInfo = computed(() => {
    if (!avgPrice.value || !currentPrice.value) return null;
    const diff = currentPrice.value - avgPrice.value;
    const abs = Math.abs(diff);
    if (abs < 1) return null;
    const pct = Math.round((diff / avgPrice.value) * 100);
    if (pct === 0) return null;
    return { isGoodDeal: diff < 0, savings: abs, percentDiff: pct };
});

const barometer = computed(() => {
    if (!priceMin.value || !priceMax.value || !currentPrice.value) return null;
    const r = priceMax.value - priceMin.value;
    if (r < 1) return null;
    return {
        current: currentPrice.value,
        min: priceMin.value,
        max: priceMax.value,
        position: Math.max(
            0,
            Math.min(100, ((currentPrice.value - priceMin.value) / r) * 100),
        ),
    };
});

const barometerColor = computed(() => {
    if (!barometer.value) return "#10b981";
    const p = barometer.value.position / 100;
    if (p < 0.5) return lerpColor("#10b981", "#f59e0b", p * 2);
    return lerpColor("#f59e0b", "#e34444", (p - 0.5) * 2);
});

function lerpColor(a: string, b: string, t: number): string {
    const parse = (c: string) => [
        parseInt(c.slice(1, 3), 16),
        parseInt(c.slice(3, 5), 16),
        parseInt(c.slice(5, 7), 16),
    ];
    const [r1 = 0, g1 = 0, b1 = 0] = parse(a);
    const [r2 = 0, g2 = 0, b2 = 0] = parse(b);
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const bl = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r},${g},${bl})`;
}

const hasRightContent = computed(
    () => !!(savingsInfo.value || barometer.value || cheapestMerchant.value),
);

const formatPrice = (p: number) =>
    new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
    }).format(p);

const shortPrice = (p: number) =>
    new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(p);

onMounted(() => fetchHistory(selectedDays.value));
watch(
    () => props.ean,
    () => fetchHistory(selectedDays.value),
);
</script>

<style scoped>
.chart-wrapper {
    display: flex;
    align-items: stretch;
    gap: 4px;
}

.chart-area {
    flex: 1;
    min-width: 0;
}

.sparkline-svg {
    width: 100%;
    height: 160px;
    display: block;
}

.y-axis {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 8px 0;
    min-width: 70px;
    text-align: right;
}

.y-tick {
    font-size: 0.75rem;
    line-height: 1;
}

.x-axis {
    padding-right: 74px;
}

.barometer-track {
    position: relative;
    height: 12px;
    border-radius: var(--m-radius);
    background: linear-gradient(to right, #10b981, #a3d44e, #f59e0b, #e34444);
}

.barometer-marker {
    position: absolute;
    top: 50%;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    border: 3px solid #10b981;
    transform: translate(-50%, -50%);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
</style>
